const DEFAULT_API_ORIGIN = 'http://localhost:9001';
const DEFAULT_API_PREFIX = '/api';
const DEFAULT_BACKEND_PORT = '9001';

const normalizeOrigin = (origin: string) => origin.replace(/\/+$/, '');
const normalizePrefix = (prefix: string) => {
  if (!prefix) return DEFAULT_API_PREFIX;
  return prefix.startsWith('/') ? prefix : `/${prefix}`;
};

function isLoopbackHostname(hostname: string): boolean {
  return hostname === 'localhost' || hostname === '127.0.0.1';
}

/**
 * 개발 API 베이스:
 * - 브라우저가 https 이면 항상 same-origin (nginx 리버스 프록시). :9001 직접 호출 금지.
 * - REACT_APP_SAME_ORIGIN_API=1 → 브라우저 origin
 * - REACT_APP_API_ORIGIN / REACT_APP_API_URL (http 개발용). https 페이지에서는 무시.
 * - PC localhost:3500 → CRA /api 프록시(동일 origin)
 * - 폰 등 LAN IP(http) → 같은 PC의 :9001 직접
 */
function resolveHttpApiBase(): string {
  const fromEnv =
    process.env.REACT_APP_API_ORIGIN || process.env.REACT_APP_API_URL;

  const sameOrigin =
    process.env.REACT_APP_SAME_ORIGIN_API === 'true' ||
    process.env.REACT_APP_SAME_ORIGIN_API === '1';

  // nginx HTTPS: 페이지가 https 면 무조건 origin (API_URL=127.0.0.1:9001 이어도)
  if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
    return normalizeOrigin(window.location.origin);
  }

  if (typeof window !== 'undefined' && sameOrigin) {
    return normalizeOrigin(window.location.origin);
  }

  if (fromEnv) {
    return normalizeOrigin(fromEnv);
  }

  if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
    const { hostname, protocol, port } = window.location;
    if (hostname && !isLoopbackHostname(hostname)) {
      return normalizeOrigin(`${protocol}//${hostname}:${DEFAULT_BACKEND_PORT}`);
    }
    if ((port === '3500' || port === '3000') && hostname) {
      return normalizeOrigin(`${protocol}//${hostname}:${port}`);
    }
  }

  if (typeof window !== 'undefined' && process.env.NODE_ENV === 'production') {
    return normalizeOrigin(window.location.origin);
  }

  return DEFAULT_API_ORIGIN;
}

/** WebSocket origin only (no path). Path /ws is CRA HMR — never part of origin. */
function resolveWsOrigin(httpBase: string): string {
  const fromEnv =
    process.env.REACT_APP_WS_ORIGIN ||
    process.env.REACT_APP_WS_URL_BASE ||
    process.env.REACT_APP_WS_URL;
  if (fromEnv) {
    const raw = fromEnv.startsWith('ws') || fromEnv.startsWith('http')
      ? fromEnv
      : `ws://${fromEnv}`;
    try {
      const u = new URL(raw.replace(/^http/, 'ws'));
      const proto =
        u.protocol === 'https:' || u.protocol === 'wss:' ? 'wss:' : 'ws:';
      // host only — strip /ws or any path mistaken for a full socket URL
      return normalizeOrigin(`${proto}//${u.host}`);
    } catch {
      const ws = raw.startsWith('ws') ? raw : raw.replace(/^http/, 'ws');
      return normalizeOrigin(ws.replace(/\/+$/, '').replace(/\/ws$/i, ''));
    }
  }
  // https 페이지 → 항상 wss://현재호스트 (포트 3500/9001 붙이지 않음)
  if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
    return normalizeOrigin(`wss://${window.location.host}`);
  }
  const sameOrigin =
    process.env.REACT_APP_SAME_ORIGIN_API === 'true' ||
    process.env.REACT_APP_SAME_ORIGIN_API === '1';
  if (typeof window !== 'undefined' && sameOrigin) {
    const { protocol, host } = window.location;
    const wsProto = protocol === 'https:' ? 'wss:' : 'ws:';
    return normalizeOrigin(`${wsProto}//${host}`);
  }
  if (typeof window !== 'undefined' && process.env.NODE_ENV === 'production') {
    const { protocol, host } = window.location;
    const wsProto = protocol === 'https:' ? 'wss:' : 'ws:';
    return normalizeOrigin(`${wsProto}//${host}`);
  }
  if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
    const { hostname, protocol, port } = window.location;
    const devPort = port === '3500' || port === '3000';
    if (devPort && hostname) {
      const wsProto = protocol === 'https:' ? 'wss:' : 'ws:';
      if (protocol === 'https:') {
        return normalizeOrigin(`${wsProto}//${hostname}`);
      }
      return normalizeOrigin(`${wsProto}//${hostname}:${DEFAULT_BACKEND_PORT}`);
    }
  }
  return normalizeOrigin(httpBase.replace(/^http/, 'ws'));
}

export const API_ORIGIN = resolveHttpApiBase();

export const API_PREFIX = normalizePrefix(
  process.env.REACT_APP_API_PREFIX || DEFAULT_API_PREFIX
);

export const API_BASE_URL = `${API_ORIGIN}${API_PREFIX}`;

export const WS_ORIGIN = resolveWsOrigin(API_ORIGIN);

export const config = {
  apiUrl: API_ORIGIN,
  apiPrefix: API_PREFIX,
  apiBaseUrl: API_BASE_URL,
  wsOrigin: WS_ORIGIN,
};

export const API_URL = config.apiUrl;
